import { computed, ref, Ref, UnwrapRef, UnwrapRefSimple } from 'vue';
import { Page } from '@inertiajs/core';
import { usePage, useForm, InertiaForm } from '@inertiajs/vue3';
import { useThemeStore } from '@/Stores/Theme';
import { SharedPageProps } from '@/Types/inertia';
import lodash from 'lodash';

export interface Search {
    value: string;
    regex: boolean;
}

export interface Order {
    dir: "asc" | "desc";
    column: number;
}

export interface Column {
    data: string;
    name: string;
    searchable: boolean;
    orderable: boolean;
    visible: boolean;
    search: Search;
    attributes?: Record<string, any>; // Optional for attributes in `columns`
    title?: string; // Optional for title in `columns`
}

export interface Request {
    search: Search;
    order: Order[];
    columns: Column[];
    start: number;
    length: number;
    [key: string]: any;
}

export interface Datatable<T = any, A = undefined> {
    draw: number;
    recordsTotal: number;
    recordsFiltered: number;
    data: T[]; // Generic type for the data
    request: Request;
    disableOrdering: boolean;
    input: Request;
    additionalData: A;
}

export interface TableResponse<T = any, A = undefined> {
    datatable: Datatable<T, A>;
    columns: Column[];
}

export type RequestOptions = {
    draw?: number,
    start?: number,
    length?: number,
    search: {
        value: string,
        regex: boolean,
    },
    order: Array<{
        dir: 'asc' | 'desc',
        column: number,
    }>,
    columns?: Array<{
        data?: string,
        name?: string,
        searchable?: boolean,
        orderable?: boolean,
        search?: {
            value: string,
            regex: boolean,
        },
    }>,
    [key: string]: any;
};

export type Header = {
    key: ("data-table-group" | "data-table-select" | "data-table-expand" | (string & {})) | undefined;
    title?: string | undefined;
    align?: ("start" | "end" | "center") | undefined;
    sortable?: boolean | undefined;
    headerProps: {
      hasHtml: boolean;
      [x: string]: any;
    };
};

export interface UseDatatableReturn<T = any, A = undefined> {
    loading: Ref<boolean>;
    request: InertiaForm<RequestOptions>;
    headers: Array<Header>;
    htmlColumns: string[];
    records: Ref<UnwrapRef<T[]>>;
    totalRecords: Ref<number>;
    allTotalRecords: Ref<number>;
    totalPage: Ref<number>;
    currentPage: Ref<number>;
    itemsPerPageOptions: Array<{ value: number; title: string }>;
    sortBy: Ref<any[]>;
    config: Ref<DatatableConfig>;
    onUpdatePage: (pageNumber: number) => void;
    onUpdateItemsPerPage: (num: number) => void;
    onUpdateOptions: () => void;
    onSort: (data: any) => void;
    onSearch: (bounce?: boolean) => void;
    redraw: () => void;
    setTable: (_table: any) => void;
    modifyColumn: (field: keyof T, data: T[keyof T], matchValue: T[keyof T], matchColumn?: keyof T | 'id') => void;
    replaceRow: (newRow: T, matchColumn?: keyof T | 'id') => void;
    afterDefault: Ref<() => void>;
    selectedRows: Ref<(number | string)[]>;
    clearSelection: () => void;
    additionalData: Ref<A>;
}

export type DatatableConfig = {
    ability?:{
        orderable?: boolean,
        searchable?: boolean,
        pagination?: boolean,
        length?: boolean,
        footer?: boolean,
        highlight?: {
            enable?: boolean,
            full?: boolean,
        },
        selection?: boolean,
    },
    paging?:{
        length?: number,
    },
    additionalFilters?: {
        [key: string]: any;
    },
    
}

  
export function useDatatable<T, A = undefined>(route: string, table?: TableResponse<T, A>, _config?: DatatableConfig) : UseDatatableReturn<T, A>{
    _config = _config ?? {};
    _config.ability = {
        orderable: _config.ability?.orderable ?? true,
        searchable: _config.ability?.searchable ?? true,
        pagination: _config.ability?.pagination ?? true,
        length: _config.ability?.length ?? true,
        footer: _config.ability?.footer ?? true,
        selection: _config.ability?.selection ?? false,
        highlight: {
            enable: _config.ability?.highlight?.enable ?? true,
            full: _config.ability?.highlight?.full ?? true,
        },
    };
    _config.paging = {
        length: _config?.paging?.length ?? 10,
    };
    _config.additionalFilters = _config.additionalFilters ?? {};

    let config = ref<DatatableConfig>(_config);
    let itemsPerPage: number = table?.datatable?.request?.length || config?.value.paging?.length || 10;
    // console.log('itemsPerPage', itemsPerPage)
    let start: number = table?.datatable?.request?.start ?? 0;
    let itemsPerPageOptions = [
        {value: 2, title: '2'},
        {value: 10, title: '10'},
        {value: 25, title: '25'},
        {value: 50, title: '50'},
        {value: 100, title: '100'},
        {value: 200, title: '200'},
        {value: 500, title: '500'},
    ];
    const defaultRequestOptions: RequestOptions = {
        draw: table?.datatable?.draw ?? 1,
        start: start,
        length: itemsPerPage,
        search:{
            value: table?.datatable?.request?.search?.value ?? '',
            regex: table?.datatable?.request?.search?.regex ?? false,
        },
        order: [
            {
                dir: table?.datatable?.request?.order?.[0]?.dir ?? 'desc',
                column:  table?.datatable?.request?.order?.[0]?.column ?? 0,   
            }
        ],
        columns: [],
        additionalFilters: _config.additionalFilters,
    };
    // const requestOptions: RequestOptions = lodash.merge(defaultRequestOptions, _requestOptions);
    const page = usePage<SharedPageProps>();
    const themeStore = useThemeStore();
    const request = useForm<RequestOptions>(defaultRequestOptions);
    const loading = ref(false);
    const columnIndexes: any = {};
    let headers: Array<Header> = []; 
    let htmlColumns: any = [];
    const sortBy: any = ref([]);
    if(table?.columns){
        for(let [i, item] of table.columns.entries()){
            columnIndexes[item.data] = i;
            request.columns?.push({
                data: item.data,
                name: item.name,
                searchable: config.value.ability?.searchable ? (item?.searchable ?? true) : false,
                orderable: config.value.ability?.orderable ? (item?.orderable ?? true) : false,
                search: {
                    value: '',
                    regex: false,
                },
            });
            let isVisible = item?.visible ?? true;
            if(isVisible){
                if(request.order[0].column == i){
                    sortBy.value = [{
                        key: item.data,
                        order: request.order[0].dir,
                    }];
                }
                headers.push({
                    title: item.title,
                    align: item?.attributes?.align ?? 'start',
                    sortable: config.value.ability?.orderable ? (item?.orderable ?? false) : false,
                    key: item.data,
                    headerProps:{
                        hasHtml: item?.attributes?.hasHtml ?? false,
                    },
                });
                if(item?.attributes?.hasHtml){
                    htmlColumns.push(item.data);
                }
            }
        }
    }

    // const records = ref<T[]>(table?.datatable?.data ?? []);
    const records = ref<T[]>(table?.datatable?.data ?? []);
    // const records = ref<UnwrapRef<T>[]>(table?.datatable?.data ?? []);
    const allTotalRecords = ref(0);
    const totalRecords = ref(0);
    const currentPage = ref(0);
    const totalPage = ref(0);
    const selectedRows: Ref<(number | string)[]> = ref([]);
    const additionalData = ref<A>(table?.datatable?.additionalData ?? (undefined as unknown as A));

    const clearSelection = () => {
        selectedRows.value = [];
    }

    const calculateState = () => {
        start = table?.datatable?.request?.start ?? 0;
        itemsPerPage = table?.datatable?.request?.length || 10;
        totalRecords.value = table?.datatable?.recordsFiltered ?? 0;
        allTotalRecords.value = table?.datatable?.recordsTotal ?? 0;
        currentPage.value = itemsPerPage ? ((start ?? 0) / (itemsPerPage ?? 0)) + 1 : 1 ;
        totalPage.value = itemsPerPage ? (totalRecords.value / (itemsPerPage ?? 0)) : 1 ;
        if(totalPage.value <= 0){
            totalPage.value = 1;
        }
        totalPage.value = Math.ceil(totalPage.value);
        additionalData.value = table?.datatable?.additionalData;
        setTimeout(function(){
            afterDefault.value();
        }, 0);
    }
    calculateState();

    const makeRequest = () => {
        console.trace('request', request.data());
        // return;
        loading.value = true;
        request.draw = (request.draw ?? 0) + 1;
        request.get(route, {
            preserveScroll: true,
            preserveState: true,
            queryStringArrayFormat: 'indices',
            onSuccess: (page: Page<SharedPageProps>) => {
                // console.log('onSuccess',page)
                table = page.props.data?.table;
                records.value = table?.datatable?.data ?? [];
                calculateState();
                
                // console.log(selectedRows.value)
            },
            onFinish: () => {
                loading.value = false;
            }
        });
    }

    const setTable = (_table: any) => {
        table = _table;
        records.value = table?.datatable?.data ?? [];
        calculateState();
    }

    const filterBource = ref<number>();
    const onSearch = (bounce: boolean = true) => {
        loading.value = true;
        clearTimeout(filterBource.value);
		filterBource.value = setTimeout(() => {
            loading.value = false;
			clearTimeout(filterBource.value);
            request.start = 0;
			makeRequest();
		}, bounce ? 500 : 0);
    }

    const onUpdatePage = (pageNumber: number) => {
        request.start = (pageNumber - 1) * (request.length ?? 10);
        
        makeRequest();
    }

    const redraw = () => {
        makeRequest();
    }

    const onUpdateItemsPerPage = (num: number) => {
        onUpdatePage(currentPage.value);
    }

    const onSort = (data: any) => {
        if(data?.[0]?.key){
            request.order = [{
                column: columnIndexes[data[0].key],
                dir: data[0].order,
            }];
            sortBy.value = data;
        }
        else{
            request.order = [
                {
                    dir: 'desc',
                    column: 0,   
                }
            ];
            sortBy.value = [];
        }
        makeRequest();
    }

    const skipIntialUpdateOption = ref(true);
    const onUpdateOptions = () => {
        if(!skipIntialUpdateOption.value){
            makeRequest();
        }
        skipIntialUpdateOption.value = false;
    }

    const modifyColumn = (
        field: keyof T,
        data: T[keyof T],
        matchValue: T[keyof T],
        matchColumn: keyof T | 'id' = 'id'
      ) => {
        records.value = records.value.map((row) => {
          // Explicitly assert row as Record<keyof T, any> to avoid indexing issues
          if ((row as Record<keyof T | 'id', any>)[matchColumn] === matchValue) {
            return {
              ...row,
              [field]: data, // Update the specified field with new data
            };
          }
          return row; // Return unchanged row if no match
        });
    };

    const replaceRow = (
        newRow: T,
        matchColumn: keyof T | 'id' = 'id'
      ) => {
        let i = -1;
        // console.log('newRow', newRow)
        // Find index of the row that matches the condition
        for (let [index, row] of records.value.entries()) {
            // console.log('row-checking', (row as Record<keyof T | 'id', any>)[matchColumn], (newRow as Record<keyof T | 'id', any>)[matchColumn]);
          if ((row as Record<keyof T | 'id', any>)[matchColumn] === (newRow as Record<keyof T | 'id', any>)[matchColumn]) {
            i = index;
            break;
          }
        }
      
        // If found, replace with a new object (to maintain reactivity)
        if (i > -1) {
          records.value[i] = { ...newRow } as UnwrapRefSimple<T>;
        }
    };
    
    const afterDefault = ref<() => void>(() => {});
    if(records.value.length){
        afterDefault.value();
    }

    
    // console.log('headers', headers);
    // console.log('records', records);
    // console.log('totalPage', totalPage, itemsPerPage);

    return {
        loading,
        request,
        headers,
        htmlColumns,
        records,
        totalRecords,
        allTotalRecords,
        totalPage,
        currentPage,
        itemsPerPageOptions,
        sortBy,
        config,
        onUpdatePage,
        onUpdateItemsPerPage,
        onUpdateOptions,
        onSort,
        onSearch,
        redraw,
        setTable,
        modifyColumn,
        replaceRow,

        afterDefault,
        selectedRows,
        clearSelection,
        additionalData: additionalData as Ref<A>,
    }
}